// App root: auth guard (dev login / Cognito sign-in when no identity), top bar with
// the customer switcher + overall progress, and the routes. Everything below the
// guard can assume an identity is present.

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { AdminOnly } from './components/AdminOnly';
import { TopBar } from './components/TopBar';
import { preferredHomeCapacity } from './components/capacity';
import { providerApi } from './provider/providerApi';
import { useMyCustomers } from './hooks/queries';
import { CenterMessage, Spinner } from './components/ui';
import { AuthCallback } from './screens/AuthCallback';
import { Landing } from './screens/Landing';
import { Login } from './screens/Login';
import { Welcome } from './screens/Welcome';
import { MAIN_CONTENT_ID, SkipLink } from './ui/SkipLink';
import { SignPage } from './sign/SignPage';
const DesignSheet = import.meta.env.DEV
  ? lazy(() => import('./ui/DesignSheet').then((m) => ({ default: m.DesignSheet })))
  : null;
import {
  CustomerEntry,
  CustomerEntryFailed,
  CustomerEntryLoading,
  useCustomerOnly,
} from './customer/CustomerEntry';

// Each audience downloads its own surface (doc 26). The customer surfaces
// carry the motion library and the two intake/reporting shells; the review
// console carries the group board, the drawer and the cost calculator. One
// bundle for both was 716 kB, and a practice owner on a phone was paying for
// the attorney's tools before seeing their own first screen.
const PortalRouter = lazy(() =>
  import('./portal/PortalRouter').then((m) => ({ default: m.PortalRouter })),
);
const ProviderPage = lazy(() =>
  import('./provider/ProviderPage').then((m) => ({ default: m.ProviderPage })),
);
const ReviewBudget = lazy(() => import('./review_budget/ReviewBudget'));
const CustomerPage = lazy(() =>
  import('./screens/CustomerPage').then((m) => ({ default: m.CustomerPage })),
);
const Legislation = lazy(() =>
  import('./screens/Legislation').then((m) => ({ default: m.Legislation })),
);
// Source notices (BL-562). A SIBLING of /c/:customerId, not a tab inside it:
// the review shell loads the binder session and cannot render without one,
// and this surface exists precisely for practices that have no binder yet.
const NoticesPage = lazy(() =>
  import('./screens/notices/NoticesPage').then((m) => ({ default: m.NoticesPage })),
);
// The operator console (BL-344): the VerityRx team's home, the add-a-practice
// wizard and the account page. Routed only through AdminOnly, and every
// endpoint behind them refuses a non-admin on the server.
const Customers = lazy(() =>
  import('./screens/Customers').then((m) => ({ default: m.Customers })),
);
const NewWorkspace = lazy(() =>
  import('./screens/NewWorkspace').then((m) => ({ default: m.NewWorkspace })),
);
const Workspace = lazy(() =>
  import('./screens/Workspace').then((m) => ({ default: m.Workspace })),
);
// Practice groups (W4, UX-REDESIGN-BRIEF.md Addendum): link a practice's
// med-spa and telehealth workspaces. A sibling of /customers/:customerId,
// not a tab inside it.
const PracticeGroup = lazy(() =>
  import('./screens/PracticeGroup').then((m) => ({ default: m.PracticeGroup })),
);
// The cross-customer queue of self-serve "also provide the other part" asks
// (A4b, BL-693 UI half) -- a sibling of the per-workspace PracticeGroup page.
const PartRequests = lazy(() =>
  import('./screens/PartRequests').then((m) => ({ default: m.PartRequests })),
);
// The research team's inbox for refused citations (FXTEAMWIDE).
const SourceRequests = lazy(() =>
  import('./screens/SourceRequests').then((m) => ({ default: m.SourceRequests })),
);
const SourceHealth = lazy(() =>
  import('./screens/SourceHealth').then((m) => ({ default: m.SourceHealth })),
);
const ProspectScopes = lazy(() =>
  import('./prospect_scope/ProspectScopes'),
);
const Users = lazy(() => import('./screens/Users').then((m) => ({ default: m.Users })));
const FirmWorkspace = lazy(() => import('./firm/FirmWorkspace').then((m) => ({ default: m.FirmWorkspace })));
const EngagementPage = lazy(() => import('./counsel_engagements/EngagementPage').then((m) => ({ default: m.EngagementPage })));
const SourceWorkLink = lazy(() => import('./counsel_cases/SourceWork').then((m) => ({ default: m.SourceWorkLink })));
const SourceWorkQueue = lazy(() => import('./counsel_cases/SourceWork'));
const CasesPage = lazy(() => import('./counsel_cases/CasesPage').then((m) => ({ default: m.CasesPage })));
const FirmReferrals = lazy(() => import('./firm/FirmReferrals').then((m) => ({ default: m.FirmReferrals })));
const PortfolioList = lazy(() => import('./portfolio/Portfolio').then((m) => ({ default: m.PortfolioList })));
const Portfolio = lazy(() => import('./portfolio/Portfolio').then((m) => ({ default: m.Portfolio })));
const PortfolioCustomerSurface = lazy(() => import('./portfolio/PortfolioCustomerSurface').then((m) => ({ default: m.PortfolioCustomerSurface })));
// The research publisher (spec §5): the team imports, acknowledges, publishes
// and withdraws the shared research. Routed only through AdminOnly, and every
// endpoint behind it refuses a non-admin on the server.
const Activation = lazy(() => import('./screens/Activation').then((m) => ({ default: m.Activation })));
const ResearchPublishing = lazy(() =>
  import('./screens/ResearchPublishing').then((m) => ({ default: m.ResearchPublishing })),
);
const ScopeLookup = lazy(() =>
  import('./screens/ScopeLookup').then((m) => ({ default: m.ScopeLookup })),
);

// Which home: the VerityRx team lands on Customers (what exists, add one);
// a reviewer lands on their queue. Decided from the server-resolved role,
// never from anything the client stores.
function Home() {
  const { isInternalAdmin, roleReady } = useAuth();
  const { data: mine, isLoading } = useMyCustomers();
  const [providerIds, setProviderIds] = useState<string[] | null>(null);
  useEffect(() => {
    if (isInternalAdmin) return;
    providerApi.mine().then((r) => setProviderIds(r.customer_ids)).catch(() => setProviderIds([]));
  }, [isInternalAdmin]);
  if (!roleReady)
    return (
      <CenterMessage>
        <Spinner /> Loading…
      </CenterMessage>
    );
  if (!isInternalAdmin && providerIds === null) return <CenterMessage><Spinner /> Loading…</CenterMessage>;
  if (!isInternalAdmin && providerIds?.length && !(mine ?? []).length)
    return <Navigate to={`/provider/${providerIds[0]}`} replace />;
  if (!isInternalAdmin) return <Landing />;
  if (preferredHomeCapacity() === 'attorney') {
    if (isLoading) return <CenterMessage><Spinner /> Loading…</CenterMessage>;
    if ((mine ?? []).some((customer) => (customer.capacities ?? [customer.my_role]).includes('attorney')))
      return <Landing />;
  }
  return <Customers />;
}

function Loading({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <CenterMessage>
          <Spinner /> Loading…
        </CenterMessage>
      }
    >
      {children}
    </Suspense>
  );
}

function Guarded() {
  const { identity, initializing } = useAuth();
  const location = useLocation();
  const [returnNote, setReturnNote] = useState(() => sessionStorage.getItem('verityrx-return-note'));
  useEffect(() => {
    const note = sessionStorage.getItem('verityrx-return-note');
    if (note) {
      setReturnNote(note);
      sessionStorage.removeItem('verityrx-return-note');
    }
  }, [location.pathname]);
  // Which door this person is shown first. Hooks must run before any early
  // return, so this is resolved here and only READ after the identity check.
  const entry = useCustomerOnly(location.pathname.startsWith('/provider/'));

  // Prod: wait for the stored OIDC session check before deciding login vs app.
  if (initializing) {
    return (
      <CenterMessage>
        <Spinner /> Loading…
      </CenterMessage>
    );
  }
  if (identity && returnNote) {
    // The note is quiet and temporary; after a denied emailed link the person
    // still reaches the normal landing and can choose their own workspace.
    return <div><p role="status" className="vx-small vx-muted" style={{ padding: 'var(--vx-4)' }}>{returnNote} <button type="button" onClick={() => setReturnNote(null)}>Continue</button></p><GuardedContent entry={entry} /></div>;
  }
  return <GuardedContent entry={entry} />;
}

function GuardedContent({ entry }: { entry: ReturnType<typeof useCustomerOnly> }) {
  const { identity } = useAuth();
  if (!identity) return <Login />;

  // A practice signing in must land in its own workspace, not in the review
  // console (see CustomerEntry for why this is routing and not authorization).
  if (entry.loading) return <CustomerEntryLoading />;
  // The list could not be read: nothing below is opened until it can be.
  // Falling through to the console here is what looped BL-596's spinner,
  // and it showed a practice the attorney's surface on the way.
  if (entry.failed)
    return <CustomerEntryFailed error={entry.failed} retry={entry.retry} />;
  if (entry.customerIds)
    return (
      <Loading>
        <Routes>
          <Route path="/scope" element={<Loading><ScopeLookup /></Loading>} />
          <Route path="/portal/:customerId" element={<PortalRouter />} />
          <Route path="/provider/:customerId" element={<ProviderPage />} />
          <Route path="/portfolios" element={<PortfolioCustomerSurface customerId={entry.customerIds[0]} />} />
          <Route path="/portfolios/:id" element={<PortfolioCustomerSurface customerId={entry.customerIds[0]} />} />
          <Route path="*" element={<CustomerEntry />} />
        </Routes>
      </Loading>
    );

  return (
    <Routes>
          <Route path="/scope" element={<Loading><ScopeLookup /></Loading>} />
      <Route path="/provider/:customerId" element={<Loading><ProviderPage /></Loading>} />
      {/* THE CUSTOMER SURFACE RENDERS OUTSIDE THE REVIEW SHELL.
          The attorney chrome is wrong for a customer in three specific ways:
          it is branded "Compliance review", it carries a switcher listing every
          customer the viewer can reach (fine for counsel and an internal
          admin, disorienting for a practice looking at its own workspace),
          and it shows a dispositioned-progress bar that belongs to the
          reviewer's job, not the practice's. Authorization is unchanged —
          it lives per-grant on the backend, not in the chrome. */}
      <Route
        path="/portal/:customerId"
        element={
          <Loading>
            <PortalRouter />
          </Loading>
        }
      />
      <Route
        path="*"
        element={
          /* The console wears the customer design system at working density
             (doc 26): `.vx` brings the tokens and primitives, `.vx--dense`
             the 14px body and 36px controls of an eight-hour tool. */
          <div className="app-shell vx vx--dense">
            <SkipLink />
            <TopBar />
            <main className="page" id={MAIN_CONTENT_ID} tabIndex={-1}>
              <Loading>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/c/:customerId/review-budget/:sessionId" element={<Loading><ReviewBudget /></Loading>} />
                  <Route path="/c/:customerId" element={<CustomerPage />} />
                  <Route path="/firms/:firmId" element={<FirmWorkspace />} />
                  <Route path="/c/:customerId/engagement/:firmId" element={<EngagementPage />} />
                  <Route path="/c/:customerId/cases" element={<CasesPage />} />
                  <Route path="/c/:customerId/cases/:caseId" element={<CasesPage />} />
                  <Route path="/admin/firm-referrals" element={<AdminOnly><FirmReferrals /></AdminOnly>} />
                  <Route path="/admin/prospect-scopes" element={<AdminOnly><ProspectScopes /></AdminOnly>} />
                  {/* Both inside the console chrome and outside SessionShell.
                      Listed BEFORE nothing in particular — react-router
                      matches on specificity, so the queue and the detail
                      cannot shadow the binder review or each other. */}
                  <Route
                    path="/c/:customerId/notices"
                    element={<NoticesPage />}
                  />
                  <Route
                    path="/c/:customerId/notices/:noticeId"
                    element={<NoticesPage />}
                  />
                  <Route path="/legislation" element={<Legislation />} />
                  <Route path="/admin/source-health" element={<AdminOnly><SourceWorkLink /><SourceHealth /></AdminOnly>} />
                  <Route path="/portfolios" element={<PortfolioList />} />
                  <Route path="/portfolios/:id" element={<Portfolio />} />
                  <Route
                    path="/customers/new"
                    element={
                      <AdminOnly>
                        <NewWorkspace />
                      </AdminOnly>
                    }
                  />
                  <Route
                    path="/customers/:customerId"
                    element={
                      <AdminOnly>
                        <Workspace />
                      </AdminOnly>
                    }
                  />
                  <Route
                    path="/customers/:customerId/practice-group"
                    element={
                      <AdminOnly>
                        <PracticeGroup />
                      </AdminOnly>
                    }
                  />
                  <Route
                    path="/admin/source-requests"
                    element={
                      <AdminOnly>
                        <SourceRequests />
                      </AdminOnly>
                    }
                  />
                  <Route
                    path="/admin/part-requests"
                    element={
                      <AdminOnly>
                        <PartRequests />
                      </AdminOnly>
                    }
                  />
                  {/* The old four-card admin console; its work now lives on
                      the account page and in the wizard. */}
                  <Route path="/admin" element={<Navigate to="/" replace />} />
                  <Route
                    path="/admin/users"
                    element={
                      <AdminOnly>
                        <Users />
                      </AdminOnly>
                    }
                  />
                  <Route
                    path="/admin/activation"
                    element={<AdminOnly><Activation /></AdminOnly>}
                  />
                  <Route path="/admin/source-review-work" element={<AdminOnly><SourceWorkQueue /></AdminOnly>} />
                  <Route
                    path="/admin/research"
                    element={
                      <AdminOnly>
                        <ResearchPublishing />
                      </AdminOnly>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Loading>
            </main>
          </div>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <Routes>
      {DesignSheet && <Route path="/__design" element={<Loading><DesignSheet /></Loading>} />}
      {/* The Cognito redirect target must resolve before the identity exists. */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* "Your first sign-in": public, linked from the invitation email, read
          before any identity exists (2026-09-04 UX review). */}
      <Route path="/welcome" element={<Welcome />} />
      {/* The agreement signer's page: public, the token in the URL fragment. */}
      <Route path="/sign" element={<SignPage />} />
      <Route path="/*" element={<Guarded />} />
    </Routes>
  );
}
